import {
  createStyles,
  Grid,
  makeStyles,
  Theme,
  Typography
} from '@material-ui/core';
import {getClinicDetails} from '@shared/services/ClinicServices';
import {getGuarantorPaymentPlanDetails} from '@shared/services/DueInvoiceServices';
import React, {ReactElement, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';
import {APP} from 'src/style/variable';
import ActivePaymentPlanDetailsCard from '../ActivePaymentPlanDetailsCard/ActivePaymentPlanDetailsCard';
import PaymentPlanCardDetails from '../PaymentPlanCard/PaymentPlanCardDetails';
import PreviousPaymentPlanDetailsCard from '../PreviousPaymentPlanDetailsCard/PreviousPaymentPlanDetailsCard';
import {CommunicationService} from 'src/service/CommunicationService';
import {Subscription} from 'rxjs';

const styles = makeStyles((theme: Theme) =>
  createStyles({
    DrawerInfo: {
      paddingBottom: '1rem',
      paddingTop: '1rem',
      fontSize: '1.3rem',
      fontWeight: 600
    },
    sectionRow: {
      display: 'grid',
      gridTemplateColumns: '100%',
      fontSize: '1.3rem'
    },
    paymentNote: {
      paddingBottom: '1rem'
    },
    ButtonStyles: {
      height: '2.8rem'
    },
    cardRoot: {
      '& .MuiCardContent-root:last-child': {
        paddingBottom: '13px'
      }
    },
    contentViewStyles: {
      color: APP.STATUS.DEFAULT,
      fontSize: '1rem'
    },
    gridViewStyle: {
      marginBottom: '8px',
      width: '100%'
    }
  })
);
interface AllPaymentPlansCardProps {
  guarantorId: number | string;
  fromGuarantorPortal?: boolean;
  paymentPlan?: any;
  cancelPaymentPlan?: boolean;
  isConvenienceFeeExist?: any;
}
const AllPaymentPlansCard = (props: AllPaymentPlansCardProps): ReactElement => {
  const classes = styles();
  const {
    guarantorId,
    fromGuarantorPortal,
    paymentPlan,
    cancelPaymentPlan,
    isConvenienceFeeExist
  } = props;
  const groupId: any = useSelector((state: any) => state.auth.groupId);
  const [forceUpdate, setForceUpdate] = useState<number>(0);
  const acOfClinics: any = useSelector(
    (state: any) => state.clinics.selectedClinicId || []
  );
  const {t} = useTranslation();
  const [paymentPlanDetails, setPaymentPlanDetails]: any = useState(
    fromGuarantorPortal ? paymentPlan : {}
  );
  const [showConvenienceFee, setShowConvenienceFee]: any = useState(
    isConvenienceFeeExist ?? 0
  );
  const subscription = React.useRef<Subscription>();

  const fetchPaymentPlanDetails = async () => {
    if (guarantorId && acOfClinics.length > 0 && !fromGuarantorPortal) {
      getClinicDetails({groupId, clinicId: acOfClinics[0]}).then(res => {
        if (res && res?.data && typeof res?.data?.convenience_fee === 'number' && res?.data?.convenience_fee !== undefined && res?.data?.convenience_fee > 0) {
          setShowConvenienceFee(res?.data?.convenience_fee);
        }
      });

      getGuarantorPaymentPlanDetails(groupId, acOfClinics[0], guarantorId).then(
        async (res: any) => {
          if (res && res?.result) {
            setPaymentPlanDetails(res?.result);
          }
        }
      );
    }
  };
  const forceUpdateHandler = () => {
    setForceUpdate(forceUpdate + 1);
  };

  React.useEffect(() => {
    // if there is an update available and no state passed
    subscription.current = CommunicationService.getEvent().subscribe(data => {
      if (data.action === 'GUARANTOR_PAYMENT_COMPLETED') {
        fetchPaymentPlanDetails();
      }
    });
    return () => {
      subscription.current?.unsubscribe();
    };
    // eslint-disable-next-line
  }, []);

  React.useEffect(() => {
    fetchPaymentPlanDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guarantorId, acOfClinics[0], forceUpdate]);

  React.useEffect(() => {
    forceUpdateHandler();
    setPaymentPlanDetails(paymentPlan);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentPlan, Object.keys(paymentPlan ?? {}).length]);
  return (
    <>
      {
        !!paymentPlanDetails?.inProgressPlans?.length && (
          <div>
            <Typography
              className={[classes.sectionRow, classes.DrawerInfo].join(' ')}>
              {t('PAYMENT_PLANS.ACTIVE_PAYMENT_PLAN')}
            </Typography>

            <Typography className={[classes.paymentNote].join(' ')}>
              {t('PAYMENT_PLANS.ACTIVE_PAYMENT_PLAN_SUBTITLE')}
            </Typography>
            {paymentPlanDetails?.inProgressPlans?.map(
              (item: any, index: number) => {
                return (
                  <div key={index}>
                    <Grid className={classes.gridViewStyle}>
                      <PaymentPlanCardDetails
                        activePlandetails={item}
                        guarantorId={guarantorId}
                        totalPaidAmount={item?.paidInstallmentsTotalAmount}
                        totalPlanAmount={item?.totalInstallmentsAmount}
                        paymentPlanDetails={paymentPlanDetails}
                        forceUpdateHandler={forceUpdateHandler}
                        cancelPaymentPlan={cancelPaymentPlan}
                      />
                    </Grid>
                    <ActivePaymentPlanDetailsCard
                      showRetryInstalment={true}
                      activePlanInstalmentDetails={item?.plan_execution_history}
                      isConvenienceFeeExist={showConvenienceFee}
                      isGuarantorPortal={cancelPaymentPlan}
                      forceUpdateHandler={forceUpdateHandler}
                    />
                  </div>
                );
              }
            )}
          </div>
        )
        // KEPT COMMENTED CODE FOR FUTURE REFERENCE
        //  : (
        //   <>
        //     <Typography
        //       className={[classes.sectionRow, classes.DrawerInfo].join(' ')}>
        //       {t('PAYMENT_PLANS.ACTIVE_PAYMENT_PLAN')}
        //     </Typography>
        //     <Typography className={[classes.paymentNote].join(' ')}>
        //       {t('PAYMENT_PLANS.ACTIVE_PAYMENT_PLAN_SUBTITLE')}
        //     </Typography>
        //     <Card className={classes.cardRoot}>
        //       <CardContent className={classes.contentViewStyles}>
        //         {t('PAYMENT_PLANS.NO_ACTIVE_PLANS')}
        //       </CardContent>
        //     </Card>
        //   </>
        // )
      }
      {/* COMPLETE PLANS AND CANCEL PLANS */}
      {!!(
        paymentPlanDetails?.completedPlans?.length ||
        paymentPlanDetails?.cancelledPlans?.length
      ) && (
        <div>
          <Typography
            className={[classes.sectionRow, classes.DrawerInfo].join(' ')}>
            {t('PAYMENT_PLANS.PREVIOUS_PAYMENT_PLAN')}
          </Typography>
          <Typography className={[classes.paymentNote].join(' ')}>
            {t('PAYMENT_PLANS.PREVIOUS_PAYMENT_PLAN_SUBTITLE')}
          </Typography>
          <PreviousPaymentPlanDetailsCard
            completedPlanDetails={paymentPlanDetails?.completedPlans}
            cancelledPlanDetails={paymentPlanDetails?.cancelledPlans}
            totalPaidAmount={paymentPlanDetails?.paidPlanAmount}
            isConvenienceFeeExist={showConvenienceFee}
            isGuarantorPortal={cancelPaymentPlan}
            forceUpdateHandler={forceUpdateHandler}
          />
        </div>
      )}
    </>
  );
};

export default AllPaymentPlansCard;
